<template>
  <div class="npvContainer">
    <v-card class="card">
      <div class="headerContainer">
        <span class="headerTitle">Simulations</span>
        <div class="actionsTitleContainer">
          <v-text-field
            v-model="search"
            prepend-inner-icon="search"
            :label="$t('Search')"
            :placeholder="$t('Search')"
            single-line
            class="layer-search"
          />
          <v-btn class="addSimulationButton" @click="addSimulationHandler">
            <v-icon left> mdi-plus </v-icon>
            Add Simulation
          </v-btn>
        </div>
      </div>
      <div class="tableContainer">
        <v-data-table
          :headers="headersT"
          :items="simulations"
          :search="search"
          :loading="loading"
          loading-text=""
          :page.sync="paginationWeb.page"
          :sort-by="paginationWeb.sortBy"
          :sort-desc="paginationWeb.descending"
          :items-per-page.sync="paginationWeb.rowsPerPage"
          :items-per-page-text="rowsPerPageText"
          :items-per-page-items="rowsPerPageItems"
          :class="{
            'no-show-permission': !userHasPermissionTo('show'),
          }"
          :header-props="{ sortIcon }"
          :footer-props="{
            prevIcon,
            nextIcon,
            'items-per-page-options': rowsPerPageItems,
          }"
          @pagination="
            loadingSpinners(dataTableUserWebListId, paginationWeb.rowsPerPage)
          "
          hide-default-header
        >
          <template v-slot:header>
            <thead>
              <tr>
                <table-header-cell
                  :header="mainHeadersWeb[0]"
                  @sort="changeSort"
                  :filterActive="!!filters.name"
                  :pagination="paginationWeb"
                  :sort-icon="
                    paginationWeb.descending &&
                    mainHeadersWeb[0].value === paginationWeb.sortBy
                      ? 'arrow_drop_up'
                      : 'arrow_drop_down'
                  "
                  style="
                    padding-left: 39px;
                    background-color: rgba(103, 160, 180, 0.15);
                  "
                >
                  <v-text-field
                    v-model="filters.label"
                    :label="'Label'"
                    clearable
                    class="text-field"
                  />
                </table-header-cell>

                <table-header-cell
                  :header="mainHeadersWeb[1]"
                  @sort="changeSort"
                  :pagination="paginationWeb"
                  :filterActive="!!filters.username"
                  :sort-icon="
                    paginationWeb.descending &&
                    mainHeadersWeb[1].value === paginationWeb.sortBy
                      ? 'arrow_drop_up'
                      : 'arrow_drop_down'
                  "
                  style="background-color: rgba(103, 160, 180, 0.15)"
                >
                  <v-text-field
                    v-model="filters.workspace"
                    :label="'Workspace'"
                    clearable
                    class="text-field"
                  />
                </table-header-cell>

                <table-header-cell
                  :header="mainHeadersWeb[2]"
                  @sort="changeSort"
                  :pagination="paginationWeb"
                  :filterActive="!!filters.email"
                  :sort-icon="
                    paginationWeb.descending &&
                    mainHeadersWeb[2].value === paginationWeb.sortBy
                      ? 'arrow_drop_up'
                      : 'arrow_drop_down'
                  "
                  style="background-color: rgba(103, 160, 180, 0.15)"
                >
                  <v-text-field
                    v-model="filters.email"
                    :label="'User'"
                    clearable
                    class="text-field"
                  />
                </table-header-cell>

                <table-header-cell
                  :header="mainHeadersWeb[3]"
                  @sort="changeSort"
                  :pagination="paginationWeb"
                  :filterActive="!!filters.status"
                  :sort-icon="
                    paginationWeb.descending &&
                    mainHeadersWeb[3].value === paginationWeb.sortBy
                      ? 'arrow_drop_up'
                      : 'arrow_drop_down'
                  "
                  style="background-color: rgba(103, 160, 180, 0.15)"
                >
                  <v-text-field
                    v-model="filters.lastUpdated"
                    :label="'Last Updated'"
                    clearable
                    class="text-field"
                  />
                </table-header-cell>

                <table-header-cell
                  :header="mainHeadersWeb[4]"
                  @sort="changeSort"
                  :pagination="paginationWeb"
                  :filterActive="false"
                  :sort-icon="
                    paginationWeb.descending &&
                    mainHeadersWeb[3].value === paginationWeb.sortBy
                      ? 'arrow_drop_up'
                      : 'arrow_drop_down'
                  "
                  style="background-color: rgba(103, 160, 180, 0.15)"
                >
                  <v-text-field
                    v-model="filters.status"
                    :label="'Status'"
                    clearable
                    class="text-field"
                  />
                </table-header-cell>

                <table-header-cell
                  :header="mainHeadersWeb[5]"
                  :pagination="paginationWeb"
                  :filterActive="false"
                  :sort-icon="''"
                  style="
                    padding-left: 20px;
                    background-color: rgba(103, 160, 180, 0.15);
                  "
                />
              </tr>
            </thead>
          </template>

          <template v-slot:item="props">
            <tr
              :class="{
                'cursor-pointer': userHasPermissionTo('show'),
              }"
              @click="viewItem(props.item)"
            >
              <td>{{ props.item.smlr_id }}</td>

              <td>{{ props.item.workspace_name }}</td>

              <td>
                <v-icon style="margin-right: 12px" color="#4FA2B7"
                  >mdi-account</v-icon
                >{{ props.item.user_name }}
              </td>

              <td>{{ props.item.smlr_updated_at }}</td>

              <td>{{ props.item.smlr_status }}</td>

              <td v-if="userCanEditOrRemoveItems">
                <app-datatable-action-buttons
                  :user-can-delete="isSuperAdmin"
                  @delete="askConfirmationToDeleteItem(props.item)"
                  :user-can-workspace="superAdminAndAdminPermission"
                  @workspace="openWorkspace(props.item)"
                />
              </td>
            </tr>
          </template>

          <template v-slot:pageText="props">
            {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
            {{ props.itemsLength }}
          </template>

          <template v-slot:no-results>
            <data-table-no-results :search="search" />
          </template>

          <template v-slot:no-data>
            <div v-if="loading" />
            <v-alert v-else class="data-table-no-data" icon="warning">
              {{ $t("NoDataWeb") }}
            </v-alert>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <select-workspace-dialog-box
      v-if="isWorkspaceDialogOpen"
      :workspace-options="simulationWorkspacesOptions"
      :workspaces="simulationWorkspaces"
      style="z-index: 10"
      @close="closeSelectWorkspaceDialogBox"
      @confirm="openOnWorkspace"
    />
    <app-dialog-box
      :open="isDialogOpen"
      :title="$t('DeleteSimulation')"
      :content="$t('DeleteSimulationMessage')"
      :main-action-label="$t('Yes')"
      @mainAction="deleteSimulation"
      :secondary-action-label="$t('Cancel')"
      @secondaryAction="isDialogOpen = false"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import TableHeaderCell from "@/components/FieldManagement/FieldWork/Data/ActivityListTableHeaderCell";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import SelectWorkspaceDialogBox from "@/components/Network/Content/SelectWorkspaceDialogBox";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
export default {
  name: "NpvTable",

  components: {
    TableHeaderCell,
    DataTableNoResults,
    AppDatatableActionButtons,
    SelectWorkspaceDialogBox,
    AppDialogBox
  },

  mixins: [managementDatatableMixin],

  data() {
    return {
      isDialogOpen: false,
      isWorkspaceDialogOpen: false,
      simulationWorkspaces: [],
      simulationWorkspacesOptions: [],
      search: "",
      // users: this.$store.getters.users,
      workspaces: this.$store.getters.allWorkspaces,
      workspaceIncludes: {fields: null, includes: null},
      dataH: [],
      dataTableUserWebListId: "dataTableNpvWebListId",
      dataTableUserMobileListId: "dataTableNpvMobileListId",
      simulations: [],
      filters: {
        label: undefined,
        workspace: undefined,
        user: undefined,
        lastUpdated: undefined,
        status: undefined,
      },
      paginationWeb: {
        sortBy: "name",
        descending: false,
        page: 1,
        rowsPerPage: 15,
      },
      headersT: [
        { text: "Label", value: "smlr_id", class: "tableHeader" },
        {
          text: "Workspace",
          value: "workspace_name",
          class: "tableHeader",
          hasFilter: true,
        },
        {
          text: "User",
          value: "user_name",
          class: "tableHeader",
          hasFilter: true,
        },
        { text: "Last Update", value: "smlr_updated_at", class: "tableHeader" },
        {
          text: "Status",
          value: "smlr_status",
          class: "tableHeader",
          hasFilter: true,
        },
      ],
    };
  },


  computed: {
    mainHeadersWeb() {
      return this.getHeaders(this.headersT);
    },

    users() {
      return this.$store.getters.users
    },

    superAdminAndAdminPermission() {
      return (
        (this.isAdmin && this.userHasPermissionTo("update")) ||
        this.isSuperAdmin
      );
    },

    userCanEditOrRemoveItems() {
      return (
        this.isSuperAdmin ||
        this.userHasPermissionTo("update") ||
        this.userHasPermissionTo("destroy")
      );
    },
  },

  mounted() {
    this.getSimulationsList();
  },

  methods: {
    ...mapActions(["GET_SIMULATIONS","DELETE_SIMULATIONS", "fetchAllWorkspaces", "GET_WORKSPACES_SIMUALTION","setCurrentWorkspace","RESET_NETWORK_DIAGRAM_STATE"]),
    addSimulationHandler(){
      this.$router.push({ name: "configuration-npv" })
    },
    async getSimulationsList() {
      try {
        let resp = await this.GET_SIMULATIONS();
        let respW = await this.fetchAllWorkspaces(this.workspaceIncludes)
        this.allWorkspaces = respW;
        this.simulations = resp;
        this.simulations.forEach((simulation) => {
          const user = this.users.find(
            (user) => user.id === simulation.smlr_user_id
          );
          if (user) {
            simulation.user_name = `${user.first_name} ${user.last_name}`;
          }
          const workspace = this.allWorkspaces.find(
            (workspace) => workspace.wp_id === simulation.smlr_workspace_id
          );
          if (workspace) {
            simulation.workspace_name = `${workspace.wp_label}`;
          }
        });
      } catch (error) {
        console.error(error);
      }
    },

    loadingSpinners(id, rows) {
      localStorage.setItem(id, rows);
      this.loading = true;
      setTimeout(() => {
        this.loading = false;
      }, 300);
    },

    viewItem(item) {
      this.$router.push({
        name: "Configuration",
        params: { uuid: item.smlr_uuid },
      });
    },

    getHeaders(mainHeaders) {
      let headers = mainHeaders;
      if (this.userCanEditOrRemoveItems) {
        headers.push({
          text: "",
          value: "action",
          sortable: false,
          width: "19%",
        });
      }
      return headers;
    },

    changeSort(column) {
      if (this.paginationWeb.sortBy === column) {
        this.paginationWeb.descending = !this.paginationWeb.descending;
      } else {
        this.paginationWeb.sortBy = column;
        this.paginationWeb.descending = false;
      }
      if (this.paginationMobile.sortBy === column) {
        this.paginationMobile.descending = !this.paginationMobile.descending;
      } else {
        this.paginationMobile.sortBy = column;
        this.paginationMobile.descending = false;
      }
    },
    async openWorkspace(simulation){
      try{
        const workspaces = await this.GET_WORKSPACES_SIMUALTION(simulation.smlr_uuid);
        if(workspaces.length === 0){
          this.$toasted.global.toast_error('not workspaces found')
          return
        }
        const workspacesOptions = [];
        workspaces.forEach(workspace =>{
          workspacesOptions.push({
            id: workspace.wp_id,
            label: workspace.wp_label
          })
        });
        this.simulationWorkspacesOptions = [...workspacesOptions];
        this.simulationWorkspaces = [...workspaces];
        if(workspaces.length === 1){
          //since there is just one workspace, open it directly without dialog
          this.openOnWorkspace(workspaces[0]);
        }else{
          //open dialog so user can select workspace
          this.isWorkspaceDialogOpen = true;
        }
      }catch(err){
        this.$toasted.global.toast_error(err.response.data.message);
      }
    },
    closeSelectWorkspaceDialogBox(){
      this.isWorkspaceDialogOpen = false;
    },
    /**
     * Show objects in selected workspace
     * @param workspaceSelected
     * @returns {Promise<void>}
     */
    async openOnWorkspace(workspaceSelected) {
      try {
        await this.setCurrentWorkspace(workspaceSelected);
        await this.RESET_NETWORK_DIAGRAM_STATE();
        if (this.$route.path !== "/home") {
          this.$router.push({
            name: "home",
            params: { workspace_uuid: workspaceSelected.wp_uuid }
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isWorkspaceDialogOpen = false;
      }
    },
    askConfirmationToDeleteItem(item){
      this.isDialogOpen = true;
      this.simulationToDelete = item.smlr_uuid;
    },
    async deleteSimulation(){
      try{
        await this.DELETE_SIMULATIONS(this.simulationToDelete);
        this.simulations = this.simulations.filter(item => item.smlr_uuid !== this.simulationToDelete);
      }catch(err){
        this.$toasted.global.toast_error(err.response.data.message);
      }finally{
        this.isDialogOpen = false;
      }
    }
  },
};
</script>

<style scoped>
.npvContainer {
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow-y: scroll;
  padding-top: 70px;
  padding-left: 40px;
  padding-right: 20px;
  padding-bottom: 60px;
}

.card {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.headerContainer {
  display: flex;
  align-items: center;
  height: 90px;
  width: 100%;
  justify-content: space-between;
}

.headerTitle {
  color: #767676;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 800;
  line-height: 25px;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-left: 39px;
}

.layer-search {
  flex: 0 0 auto;
  width: 300px;
  font-size: 12px;
  margin-top: 17px;
}

.actionsTitleContainer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.addSimulationButton {
  border-radius: 8px;
  background-color: #67a0b4 !important;
  margin-right: 39px;
  margin-left: 37px;
  color: #fff;
  text-align: center;
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 26px;
  letter-spacing: 0;
  text-transform: none;
}

.tableHeader {
  background-color: rgba(103, 160, 180, 0.15) !important;
}

.tableContainer {
}

.v-data-table > .v-data-table__wrapper > table > tbody > tr > th,
.v-data-table > .v-data-table__wrapper > table > tfoot > tr > th,
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  color: #767676 !important;
  font-family: Lato;
  font-size: 12px !important;
  font-style: normal;
  font-weight: 600 !important;
  line-height: 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/Npv/NpvTable.vue